import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Brain, Plus, TrendingUp, Calendar, MessageSquare, Zap, Target } from 'lucide-react';
import api, { isAuthenticated } from '../utils/api';
import { toast } from 'sonner';

const AdminSmartDashboardPage = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [showAddPurchase, setShowAddPurchase] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [monthlyOutreach, setMonthlyOutreach] = useState([]);
  const [inquiryStats, setInquiryStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newPurchase, setNewPurchase] = useState({ purchase_date: '', product_description: '', amount: '', category: '', color: '', notes: '' });
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/admin/login'); return; }
    fetchCustomers();
    fetchInquiryStats();
  }, []);

  useEffect(() => { fetchMonthlyOutreach(); }, [selectedMonth]);

  const fetchCustomers = async () => {
    try { const r = await api.get('/customers'); setCustomers(r.data); } catch (e) {}
  };

  const fetchInquiryStats = async () => {
    try { const r = await api.get('/inquiries/stats'); setInquiryStats(r.data); } catch (e) {}
  };

  const fetchMonthlyOutreach = async () => {
    try { const r = await api.get(`/smart-dashboard/monthly-outreach?month=${selectedMonth}`); setMonthlyOutreach(r.data); } catch (e) {}
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const [pred, hist, inq] = await Promise.all([
        api.get(`/smart-dashboard/predictions/${customer.customer_id}`),
        api.get(`/purchase-history/${customer.customer_id}`),
        api.get(`/inquiries?customer_id=${customer.customer_id}`)
      ]);
      setPredictions(pred.data);
      setPurchaseHistory(hist.data);
      setInquiries(inq.data);
    } catch (e) { console.error(e); }
  };

  const handleAddPurchase = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    try {
      await api.post('/purchase-history', {
        customer_id: selectedCustomer.customer_id,
        purchase_date: new Date(newPurchase.purchase_date).toISOString(),
        product_description: newPurchase.product_description,
        amount: parseFloat(newPurchase.amount),
        category: newPurchase.category, color: newPurchase.color, notes: newPurchase.notes
      });
      toast.success('Purchase added!');
      setShowAddPurchase(false);
      setNewPurchase({ purchase_date: '', product_description: '', amount: '', category: '', color: '', notes: '' });
      handleSelectCustomer(selectedCustomer);
    } catch (e) { toast.error('Error'); }
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const filtered = customers.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.customer_id.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="px-6 py-4 flex items-center space-x-4">
          <Link to="/admin/dashboard" className="text-[#E5A93C]"><ArrowLeft className="w-6 h-6" /></Link>
          <Brain className="w-6 h-6 text-indigo-500" />
          <h1 className="font-heading text-xl font-bold text-[#2A0000]">Smart Customer Intelligence v2</h1>
          <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">Advanced ML Model</span>
        </div>
      </header>

      <div className="p-6 space-y-6">
        {/* Inquiry Stats Banner */}
        {inquiryStats && (
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg p-6">
            <h2 className="font-bold text-lg mb-4 flex items-center"><Zap className="w-5 h-5 mr-2" />WhatsApp Inquiry Intelligence</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><p className="text-xs opacity-75">Total Inquiries Tracked</p><p className="text-3xl font-bold">{inquiryStats.total_inquiries}</p></div>
              <div><p className="text-xs opacity-75">Today's Inquiries</p><p className="text-3xl font-bold">{inquiryStats.today_inquiries}</p></div>
              <div className="col-span-2"><p className="text-xs opacity-75 mb-1">Top Categories</p>
                <div className="flex flex-wrap gap-1">
                  {inquiryStats.top_categories.slice(0, 5).map((c, i) => (
                    <span key={i} className="text-xs bg-white/20 px-2 py-1 rounded">{c.category} ({c.count})</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Customer List */}
          <div className="bg-white rounded-lg border p-4">
            <h2 className="font-bold text-[#2A0000] mb-4">Customers</h2>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border rounded mb-4"
              data-testid="customer-search"
            />
            <div className="max-h-96 overflow-y-auto space-y-2">
              {filtered.map(c => (
                <button
                  key={c.customer_id}
                  onClick={() => handleSelectCustomer(c)}
                  className={`w-full text-left p-3 rounded ${selectedCustomer?.customer_id === c.customer_id ? 'bg-[#E5A93C] text-white' : 'hover:bg-gray-100'}`}
                  data-testid={`customer-${c.customer_id}`}
                >
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs opacity-75">{c.customer_id}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Intelligence Card */}
          <div className="lg:col-span-2">
            {selectedCustomer ? (
              <div className="space-y-6">
                {predictions?.predictions ? (
                  <>
                    {/* Main Prediction Card */}
                    <div className="bg-gradient-to-br from-[#2A0000] to-[#4A0404] text-white rounded-lg p-6">
                      <div className="flex justify-between mb-6">
                        <h2 className="font-heading text-2xl font-bold">{predictions.customer_name}</h2>
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            predictions.urgency_signal === 'HIGH' ? 'bg-red-500' :
                            predictions.urgency_signal === 'MEDIUM' ? 'bg-yellow-500' : 'bg-green-500'
                          }`}>
                            {predictions.urgency_signal} URGENCY
                          </span>
                          <span className="text-sm bg-[#E5A93C] px-3 py-1 rounded-full">{predictions.predictions.confidence_score}% Confidence</span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4 mb-6">
                        <div><p className="text-xs text-white/70">Purchases</p><p className="text-2xl font-bold">{predictions.total_purchases}</p></div>
                        <div><p className="text-xs text-white/70">Inquiries</p><p className="text-2xl font-bold">{predictions.total_inquiries}</p></div>
                        <div><p className="text-xs text-white/70">LTV</p><p className="text-2xl font-bold text-[#E5A93C]">₹{predictions.total_lifetime_value.toLocaleString()}</p></div>
                        <div><p className="text-xs text-white/70">Avg Order</p><p className="text-2xl font-bold text-[#E5A93C]">₹{predictions.average_order_value.toFixed(0)}</p></div>
                      </div>

                      {/* Precise Window */}
                      <div className="border-t border-white/20 pt-6 mb-6">
                        <h3 className="font-bold mb-3 flex items-center"><Target className="w-5 h-5 mr-2" /> Precise Purchase Window</h3>
                        <div className="bg-white/10 rounded-lg p-4">
                          <p className="text-sm text-white/80 mb-2">Predicted Date Range (±{Math.floor(predictions.predictions.window_days/2)} days):</p>
                          <p className="text-3xl font-bold text-[#E5A93C]">{new Date(predictions.predictions.window_start).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - {new Date(predictions.predictions.window_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          <p className="text-sm text-white/70 mt-2">Most Likely: <strong>{new Date(predictions.predictions.next_purchase_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong></p>
                        </div>
                      </div>

                      <div className="border-t border-white/20 pt-6">
                        <h3 className="font-bold mb-4 flex items-center"><TrendingUp className="w-5 h-5 mr-2" /> AI Predictions</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-white/10 p-3 rounded"><p className="text-xs text-white/70">Predicted Amount</p><p className="font-bold text-[#E5A93C] text-lg">₹{predictions.predictions.predicted_amount}</p></div>
                          <div className="bg-white/10 p-3 rounded"><p className="text-xs text-white/70">Category</p><p className="font-bold">{predictions.predictions.predicted_category}</p></div>
                          <div className="bg-white/10 p-3 rounded"><p className="text-xs text-white/70">Color Preference</p><p className="font-bold">{predictions.predictions.predicted_color}</p></div>
                          <div className="bg-white/10 p-3 rounded"><p className="text-xs text-white/70">📅 Best Outreach Date</p><p className="font-bold">{new Date(predictions.predictions.optimal_outreach_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p></div>
                        </div>
                      </div>

                      {/* Model Metadata */}
                      <div className="mt-6 pt-6 border-t border-white/20 text-xs text-white/70">
                        <p><strong>Festival Context:</strong> {predictions.festival_context.name}</p>
                        <p><strong>Avg Purchase Interval:</strong> {predictions.average_interval_days} days</p>
                        <p><strong>Inquiry → Purchase Lead Time:</strong> {predictions.inquiry_lead_time_days} days</p>
                        <p className="mt-2 italic">{predictions.predictions.model_explanation}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <p className="text-yellow-800 font-medium">Insufficient data for predictions.</p>
                    <p className="text-yellow-700 text-sm mt-1">{predictions?.message || 'Need 1+ purchase OR 2+ WhatsApp inquiries.'}</p>
                    <p className="text-xs text-yellow-600 mt-2">Signals: {predictions?.data_signals?.purchases || 0} purchases, {predictions?.data_signals?.inquiries || 0} inquiries</p>
                  </div>
                )}

                {/* Inquiry History */}
                {inquiries.length > 0 && (
                  <div className="bg-white rounded-lg border p-6">
                    <h3 className="font-bold text-[#2A0000] mb-4">📱 WhatsApp Inquiry History ({inquiries.length})</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {inquiries.slice(0, 10).map((inq, idx) => (
                        <div key={idx} className="flex justify-between p-2 bg-gray-50 rounded text-sm">
                          <div>
                            <span className="font-medium">{inq.product_category || 'General'}</span>
                            {inq.product_sku && <span className="text-gray-500 ml-2">({inq.product_sku})</span>}
                          </div>
                          <span className="text-xs text-gray-500">{new Date(inq.timestamp).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Purchase History */}
                <div className="bg-white rounded-lg border">
                  <div className="px-6 py-4 border-b flex items-center justify-between">
                    <h3 className="font-bold text-[#2A0000]">Purchase History</h3>
                    <button onClick={() => setShowAddPurchase(true)} className="flex items-center space-x-2 text-sm btn-primary py-2 px-4"><Plus className="w-4 h-4" /><span>Add Purchase</span></button>
                  </div>
                  {showAddPurchase && (
                    <form onSubmit={handleAddPurchase} className="p-6 bg-gray-50 border-b space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input type="date" value={newPurchase.purchase_date} onChange={(e) => setNewPurchase({ ...newPurchase, purchase_date: e.target.value })} required className="px-3 py-2 border rounded" />
                        <input type="number" placeholder="Amount" value={newPurchase.amount} onChange={(e) => setNewPurchase({ ...newPurchase, amount: e.target.value })} required className="px-3 py-2 border rounded" />
                        <input type="text" placeholder="Product description" value={newPurchase.product_description} onChange={(e) => setNewPurchase({ ...newPurchase, product_description: e.target.value })} required className="px-3 py-2 border rounded col-span-2" />
                        <input type="text" placeholder="Category" value={newPurchase.category} onChange={(e) => setNewPurchase({ ...newPurchase, category: e.target.value })} className="px-3 py-2 border rounded" />
                        <input type="text" placeholder="Color" value={newPurchase.color} onChange={(e) => setNewPurchase({ ...newPurchase, color: e.target.value })} className="px-3 py-2 border rounded" />
                      </div>
                      <div className="flex space-x-2">
                        <button type="submit" className="btn-primary py-2 px-4 text-sm">Save</button>
                        <button type="button" onClick={() => setShowAddPurchase(false)} className="btn-outline py-2 px-4 text-sm">Cancel</button>
                      </div>
                    </form>
                  )}
                  <div className="p-6">
                    {purchaseHistory.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No purchase history</p>
                    ) : (
                      <div className="space-y-2">
                        {purchaseHistory.map((p, idx) => (
                          <div key={idx} className="flex justify-between p-3 bg-gray-50 rounded">
                            <div>
                              <p className="font-medium text-sm">{p.product_description}</p>
                              <p className="text-xs text-gray-600">{new Date(p.purchase_date).toLocaleDateString()} {p.category && `• ${p.category}`} {p.color && `• ${p.color}`}</p>
                            </div>
                            <p className="font-bold text-[#E5A93C]">₹{p.amount}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg border p-12 text-center">
                <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Select a customer to view their AI intelligence profile</p>
              </div>
            )}
          </div>
        </div>

        {/* Monthly Outreach */}
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bold text-[#2A0000] flex items-center"><Calendar className="w-5 h-5 mr-2 text-indigo-500" />Monthly Outreach Calendar</h2>
              <p className="text-sm text-gray-500 mt-1">Customers predicted to purchase in this month — uses same AI model as individual predictions</p>
            </div>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} className="px-4 py-2 border rounded" data-testid="month-select">
              {months.map((m, idx) => <option key={idx} value={idx + 1}>{m}</option>)}
            </select>
          </div>

          {monthlyOutreach.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No customers predicted for {months[selectedMonth - 1]}</p>
              <p className="text-xs text-gray-400 mt-1">Customers appear here based on their AI-predicted purchase date</p>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                <strong>{monthlyOutreach.length}</strong> customer{monthlyOutreach.length !== 1 ? 's' : ''} predicted to purchase in {months[selectedMonth - 1]}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs uppercase">Customer</th>
                      <th className="px-3 py-2 text-left text-xs uppercase">Predicted Date</th>
                      <th className="px-3 py-2 text-left text-xs uppercase">Category</th>
                      <th className="px-3 py-2 text-right text-xs uppercase">Amount</th>
                      <th className="px-3 py-2 text-center text-xs uppercase">Urgency</th>
                      <th className="px-3 py-2 text-center text-xs uppercase">Confidence</th>
                      <th className="px-3 py-2 text-center text-xs uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {monthlyOutreach.map(c => (
                      <tr key={c.customer_id} className="hover:bg-gray-50" data-testid={`outreach-${c.customer_id}`}>
                        <td className="px-3 py-3">
                          <p className="font-medium">{c.name}</p>
                          <p className="text-xs text-gray-500">{c.customer_id}</p>
                        </td>
                        <td className="px-3 py-3">
                          <p className="font-medium">{new Date(c.predicted_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          <p className="text-xs text-gray-500">
                            Window: {new Date(c.window_start).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - {new Date(c.window_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </p>
                        </td>
                        <td className="px-3 py-3 text-gray-700">{c.predicted_category}</td>
                        <td className="px-3 py-3 text-right font-bold text-[#E5A93C]">₹{c.predicted_amount?.toLocaleString()}</td>
                        <td className="px-3 py-3 text-center">
                          <span className={`text-xs px-2 py-1 rounded font-medium ${
                            c.urgency_signal === 'HIGH' ? 'bg-red-100 text-red-700' :
                            c.urgency_signal === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {c.urgency_signal}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className="text-xs font-medium">{c.confidence_score}%</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <a
                            href={`https://wa.me/${c.phone.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${c.gender === 'Female' ? 'Mrs.' : c.gender === 'Male' ? 'Mr.' : ''} ${c.name},`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-1 bg-[#25D366] text-white px-3 py-1 rounded text-xs hover:bg-[#20BA5A]"
                            data-testid={`outreach-whatsapp-${c.customer_id}`}
                          >
                            <MessageSquare className="w-3 h-3" /><span>WhatsApp</span>
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSmartDashboardPage;
